import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(slug || 'download-guide');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const category = getCategoryBySlug(activeCategory);
  const categoryArticles = getArticlesByCategory(activeCategory);

  if (!category) {
    return (
      <div className="pt-24 pb-16 px-4 text-center">
        <h2 className="text-2xl font-bold text-foreground-900">分类未找到</h2>
        <Link to="/" className="text-primary-500 hover:underline mt-4 inline-block">
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="relative w-full h-[280px] md:h-[360px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={category.image}
            alt={category.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/20"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-6 pb-8 md:pb-12">
          <div className="max-w-7xl mx-auto">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3">
              <i className={`${category.icon} text-white text-xl`}></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white font-heading mb-2">
              {category.name}
            </h1>
            <p className="text-white/75 text-sm md:text-base max-w-xl">
              {category.description}
            </p>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 pt-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: t('breadcrumb_home'), path: '/' },
              { label: category.name },
            ]}
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="w-full px-4 md:px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Articles List */}
      <section className="py-6 md:py-10 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-foreground-300">·</span>
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.views > 10000
                          ? `${(article.views / 10000).toFixed(1)}万`
                          : article.views}
                      </span>
                    </div>
                    <h2 className="text-foreground-900 font-semibold text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors font-heading">
                      {article.title}
                    </h2>
                    <p className="text-foreground-500 text-sm line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-500"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}